import { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { FaChevronDown, FaChevronUp, FaChevronRight } from 'react-icons/fa';
import Stack from './Stack';
import TenureBadge from './TenureBadge';
import ActiveBadge from './ActiveBadge';

const getInitials = (name) =>
  name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase();

export default function TeamCardWithActions({
  team,
  competition,
  competition_event,
  userBadgeId,
  onEdit,
  onDelete
}) {
  const [expanded, setExpanded] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const isCreator = team.creator_employee_id === Number(userBadgeId);

  return (
    <div className="w-full border-t-1 py-2 sm:py-4 bg-white transition-all">
      <div
        className="flex justify-between items-center cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <Stack direction="row" justify="start" align="center">
          <h3 className="text-sm font-semibold text-gray-800">{team.name}</h3>
          <div className="w-px bg-gray-400 h-4 mx-2"></div>
          <p className="text-xs text-gray-500">
            Competition: {competition.name}, {competition_event.name}
          </p>  
        </Stack>
        
        <div className="flex items-center gap-2">
          {expanded ? (
            <>
              {isCreator && (
                <div>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevents collapsing the card
                      setAnchorEl(e.currentTarget);
                    }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right'
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right'
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        setAnchorEl(null);
                        onEdit(team);
                      }}
                    >
                      <ListItemIcon>
                        <EditIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Edit</ListItemText>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setAnchorEl(null);
                        onDelete(team);
                      }}
                    >
                      <ListItemIcon>
                        <DeleteIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Delete</ListItemText>
                    </MenuItem>
                  </Menu>
                </div>
              )}
              {/* <FaChevronUp className="text-gray-400" /> */}
            </>
          ) : (
            <>
              {isCreator && (
                <div>
                  <Stack direction='row' className="gap-2">
                    <ActiveBadge value={team.is_active} >{team.is_active ? 'Active' : 'Inactive'}</ActiveBadge>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevents collapsing the card
                        setAnchorEl(e.currentTarget);
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right'
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right'
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        setAnchorEl(null);
                        onEdit(team);
                      }}
                    >
                      <ListItemIcon>
                        <EditIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Edit</ListItemText>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setAnchorEl(null);
                        onDelete(team);
                      }}
                    >
                      <ListItemIcon>
                        <DeleteIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Delete</ListItemText>
                    </MenuItem>
                  </Menu>
                </div>
              )}
              {/* <FaChevronDown className="text-gray-400" /> */}
            </>
          )}
        </div>
      </div>

      {!expanded && (
        <Stack width="full" direction="row" justify="between" align="center">
          <p className="text-xs text-gray-400 mt-2">
            {team.competition_team_members.length} of {competition.team_size}{' '}
            participants
          </p> 

          <div className="flex items-center gap-1 text-xs text-black mt-2" onClick={() => setExpanded(true) }>
            <span className='text-xs border-b border-gray-600'>Team Details</span>
            <FaChevronRight
              className={`text-xs transition-transform duration-200 ${
                expanded ? 'rotate-90' : ''
              }`}
            />
          </div>
        </Stack>
      )}

      {expanded && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mt-4">
            {team.competition_team_members.map((member, i) => (
              <div key={i} className="flex items-center space-x-2">
                <div className="hidden w-10 h-10 rounded-full bg-purple-100 text-purple-700 items-center justify-center font-semibold">
                  {getInitials(member.name)}
                </div>
                <div className="text-sm flex flex-col justify-start items-start gap-1">
                  <div className="text-sm text-black">{member.name}</div>
                  <TenureBadge role={member.role}>{member.role}</TenureBadge>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-between items-center text-sm text-gray-500 mt-4 pt-2 border-t">
            <span>
              {team.competition_team_members.length} of {competition.team_size}{' '}
              participants
            </span>
          </div>
        </>
      )}
    </div>
  );
}
